<template>
  <div
    v-if="plan"
    :class="$style.wrapper"
  >
    <div
      v-if="plan.name === 'Professional' && fringe"
      :class="$style.mostPopular"
    >
    {{ t(`subscription.popular`) }}
    </div>
    <div :class="$style.params">
      <div :class="$style.header">
        <div :class="$style.title">{{ plan.name }}</div>
        <div :class="$style.price">
          <div :class="$style.priceValue">
            <template v-if="plan.name === 'Custom' || +pricePerMonth > 0">
              {{ plan.name === 'Custom' ? '' : `$${pricePerMonth}` }}
            </template>
            <template v-else>
              {{ t('prices.free') }}
            </template>
          </div>{{ plan.name === 'Custom' ? '' : priceDelimeter }}
        </div>
      </div>

      <div :class="$style.paramsList">
        <div
          v-for="(feature, key) in visibleFeatures"
          :key="key"
          :class="$style.paramsItem"
        >
          <div :class="$style.paramsName">{{ featureLabel(String(key)) }}</div>
          <div
            v-if="typeof feature === 'boolean'"
            :class="[$style.paramsCheckbox, feature && $style.checked]"
          >
            <UiIconsCheck v-if="feature" />
          </div>
          <div
            v-else
            :class="$style.paramsValue"
          >
            {{ feature }}
          </div>
        </div>
      </div>

      <div :class="$style.actionButton">
        <slot name="actionButton" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Decimal } from 'decimal.js';
import type { PlanItemDto } from '~/dto';
import { getCloudPlanFeatures, isCloudPlan } from '~/utils/planFeatures';

interface PropsParams {
  fringe?: boolean,
}

const { t, te } = useI18n();

withDefaults(defineProps<PropsParams>(), {
  fringe: true,
});

const activeTimeLine = inject<globalThis.Ref<'1' | '3' | '6' | '12'>>('activeTimeLine');
const plan = inject<globalThis.Ref<PlanItemDto | undefined>>('activePlan');

const visibleFeatures = computed<Record<string, unknown>>(() => {
  const features = plan?.value?.features as Record<string, unknown> | undefined;
  if (!features) return {};

  if (isCloudPlan(plan?.value)) {
    return {
      ...Object.fromEntries(getCloudPlanFeatures(features)),
      usageRate: t('prices.list.usageRate'),
    };
  }

  return features;
});

const featureLabel = (key: string) => {
  if (key === 'usageRate') return t('prices.params.usageRate');
  const i18nKey = `subscription.params.${key}`;
  return te(i18nKey) ? t(i18nKey) : key;
};

const pricePerMonth = computed<string>(() => {
  if (!plan?.value) return '';
  if (plan.value.name === 'Custom') return '';

  const timeLineValue = activeTimeLine?.value || '1';
  const price =
    plan.value.finalPrices?.[timeLineValue] ??
    plan.value.prices?.[timeLineValue];

  if (price == null) return '';

  return new Decimal(price).div(Number(timeLineValue)).toFixed(2).toString();
});
const priceDelimeter = computed<string>(() => plan?.value?.isFree ? t('subscription.delimetr.days') : t('subscription.delimetr.month'));
</script>

<style lang="scss" module>
  .wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .params {
    display: flex;
    flex-direction: column;
    padding: 32px 32px 14px 32px;
    border-radius: 24px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    background: #FFF;
    flex: 1;

    @media (max-width: 1023px) {
      padding: 32px 16px 24px 16px;
    }

    &List {
      display: flex;
      flex-direction: column;
      gap: 22px;
      margin-bottom: auto;
    }

    &Item {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    &Value {
      text-align: right;
    }

    &Checkbox {
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 10.5px;
      width: 21px;
      height: 21px;
      flex-shrink: 0;
      color: #fff;

      svg {
        width: 11px;
        height: auto;
      }

      &:not(.checked) {
        background-color: #D9D9D9;
      }

      &.checked {
        background-color: #293EFF;
      }
    }
  }

  .mostPopular {
    flex-shrink: 0;
    padding: 14px 14px 32px 14px;
    margin-bottom: -20px;
    border-radius: 24px 24px 0 0;
    border: 1px solid rgba(0, 0, 0, 0.05);
    background: #F8F8F8;
    box-shadow: 0px 15px 30px 0px rgba(0, 0, 0, 0.05);
    color: #293EFF;
    text-align: center;
    font-family: 'Gilroy';
    font-size: 24px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
  }

  .title {
    color: #121E29;
    font-size: 24px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;

    @media (max-width: 1023px) {
      font-size: 20px;
    }
  }

  .price {
    display: flex;
    align-items: baseline;
    color: #293EFF;
    font-style: normal;
    font-weight: 400;
    line-height: normal;

    @media (max-width: 1023px) {
      font-size: 12px;
    }

    &Value {
      font-size: 32px;
      font-weight: 500;

      @media (max-width: 1023px) {
        font-size: 24px;
      }
    }
  }

  .actionButton {
    margin-top: 25px;
  }
</style>